import { Controller, type Control, type FieldValues, type Path } from "react-hook-form";
import { WorkHoursTimePicker } from "@/components/ui/work-hours-time-picker";
import { formatDisplay, parseTime } from "@/lib/work-hours";

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  required?: boolean;
  /** Pass another form field's value (24h "HH:MM") to enforce End > Start. */
  minTime?: string | null;
  disabled?: boolean;
  error?: string;
}

/** Stores 24h "HH:MM" in form state (compatible with `time` Postgres columns). */
export function RHFWorkHoursTimePicker<T extends FieldValues>({
  control, name, label, required, minTime, disabled, error,
}: Props<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const display = formatDisplay(field.value as string);
        return (
          <WorkHoursTimePicker
            value={display}
            label={label}
            required={required}
            disabled={disabled}
            minTime={minTime ? formatDisplay(minTime) : undefined}
            error={error ?? fieldState.error?.message}
            onChange={(v) => {
              const p = parseTime(v);
              if (!p) { field.onChange(""); return; }
              field.onChange(
                `${String(p.hour24).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`,
              );
            }}
          />
        );
      }}
    />
  );
}
